import { useMemo } from 'react';
import { Trophy, XCircle, RotateCcw } from 'lucide-react';
import Confetti from 'react-confetti';

interface ResultModalProps {
  isOpen: boolean;
  correct: string;
  success: boolean;
  points: number;
  currentRound: number;
  totalRounds: number;
  stats: {
    guessedIron: number;
    guessedBronze: number;
    guessedSilver: number;
    guessedGold: number;
    guessedPlatinum: number;
    guessedEmerald: number;
    guessedDiamond: number;
    guessedMaster: number;
    guessedGrandmaster: number;
    guessedChallenger: number;
  } | null;
  onPlayAgain: () => void;
}

const RANK_MAPPING = [
  { key: 'guessedIron', label: 'Iron', color: 'bg-stone-500' },
  { key: 'guessedBronze', label: 'Bronze', color: 'bg-amber-900' },
  { key: 'guessedSilver', label: 'Silver', color: 'bg-zinc-400' },
  { key: 'guessedGold', label: 'Gold', color: 'bg-yellow-500' },
  { key: 'guessedPlatinum', label: 'Platinum', color: 'bg-cyan-600' },
  { key: 'guessedEmerald', label: 'Emerald', color: 'bg-emerald-500' },
  { key: 'guessedDiamond', label: 'Diamond', color: 'bg-blue-500' },
  { key: 'guessedMaster', label: 'Master', color: 'bg-purple-600' },
  { key: 'guessedGrandmaster', label: 'Grandmaster', color: 'bg-red-600' },
  { key: 'guessedChallenger', label: 'Challenger', color: 'bg-yellow-300' },
];

export function ResultModal({ isOpen, success, stats, onPlayAgain, correct, points, currentRound, totalRounds }: ResultModalProps) {
  if (!isOpen) return null;

  const totalGuesses = useMemo(() => {
    if (!stats) return 0;
    return RANK_MAPPING.reduce((sum, { key }) => sum + (stats[key as keyof typeof stats] as number || 0), 0);
  }, [stats]);

  const rankData = useMemo(() => {
    if (!stats) return [];

    return RANK_MAPPING.map((rank) => ({
      label: rank.label,
      // @ts-ignore
      count: stats[rank.key] || 0,
      color: rank.color,
      percentage: totalGuesses > 0 ? ((stats[rank.key as keyof typeof stats] as number || 0) / totalGuesses) * 100 : 0,
    }));
  }, [stats, totalGuesses]);

  return (

    <>
      {success && (
        <Confetti
          recycle={true}
          numberOfPieces={100}
          width={window.innerWidth}
          height={window.innerHeight}
        />
      )}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
        <div className="bg-card text-card-foreground border rounded-xl shadow-lg w-full max-w-2xl p-6 relative animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[90vh]">
          <div className="flex flex-col items-center mb-6">
            <div className={`flex flex-col items-center ${points > 0 ? "text-green-500" : "text-red-500"}`}>
              {points > 0 ? <Trophy size={64} className="mb-2" /> : <XCircle size={64} className="mb-2" />}
              <h2 className="text-3xl font-bold">{points} Points!</h2>
            </div>
            {!success && <p className="text-lg mt-2">The correct answer was {correct}</p>}
          </div>

          {stats && (
            <div className="space-y-4 mb-8">
              <h3 className="text-xl font-semibold text-center mb-4">What others guessed</h3>
              <div className="space-y-3">
                {rankData.map((rank) => (
                  <div key={rank.label} className="relative">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{rank.label}</span>
                      <span className="text-muted-foreground">{rank.percentage.toFixed(1)}% ({rank.count})</span>
                    </div>
                    <div className="w-full h-3 bg-secondary rounded-full overflow-hidden">
                      <div
                        className={`h-full ${rank.color} transition-all duration-500 ease-out`}
                        style={{ width: `${rank.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center text-xs text-muted-foreground mt-2">
                Total guesses: {totalGuesses}
              </div>
            </div>
          )}

          <div className="flex justify-center mt-6">
            <button
              onClick={onPlayAgain}
              className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-bold hover:brightness-110 transition-all active:scale-95"
            >
              <RotateCcw size={20} />
              {currentRound < totalRounds ? "Next Round" : "Finish"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
